import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { normaliserTexte } from '@/lib/normaliser';

const SPECIALITES_FALLBACK = [
  'Acupuncture','Addictologie','Allergologie','Anatomopathologie','Andrologie',
  'Anesthésiologie','Angiologie','Biologie médicale','Cardiologie','Chirurgie cardiaque',
  'Chirurgie générale','Chirurgie maxillo-faciale','Chirurgie orthopédique','Chirurgie pédiatrique',
  'Chirurgie plastique','Chirurgie thoracique','Chirurgie vasculaire','Dermatologie','Diabétologie',
  'Diététique et nutrition','Endocrinologie','Gastro-entérologie','Génétique médicale','Gériatrie',
  'Gynécologie','Hématologie','Homéopathie','Immunologie','Infectiologie','Kinésithérapie',
  'Médecine d\'urgence','Médecine du sport','Médecine du travail','Médecine esthétique',
  'Médecine générale','Médecine hyperbare','Médecine interne','Médecine légale',
  'Médecine nucléaire','Médecine physique et réadaptation','Médecine transfusionnelle',
  'Médecine tropicale','Néphrologie','Néonatologie','Neurochirurgie','Neurologie',
  'Obstétrique','Odontologie','Oncologie','Ophtalmologie','ORL','Orthopédie',
  'Ostéopathie','Pédiatrie','Pédopsychiatrie','Pharmacologie clinique','Pneumologie',
  'Psychiatrie','Radiologie','Radiothérapie','Rhumatologie','Santé publique',
  'Soins palliatifs','Stomatologie','Urologie',
];

interface SpecialiteSelectProps {
  value: string;
  onChange: (val: string) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export default function SpecialiteSelect({ value, onChange, required, placeholder, className }: SpecialiteSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [specialites, setSpecialites] = useState<string[]>(SPECIALITES_FALLBACK);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suppressNextFocusRef = useRef(false);
  const isSelectingRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadSpecialites();
  }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSpecialites = async () => {
    try {
      const { data, error } = await supabase
        .from('specialites')
        .select('nom')
        .order('nom');

      if (!error && data && data.length > 0) {
        setSpecialites(data.map((s: { nom: string }) => s.nom));
      }
    } catch {
      // fallback already set
    }
  };

  const saveNewSpecialite = async (nom: string) => {
    try {
      await supabase.from('specialites').insert({ nom }).select().single();
      setSpecialites((prev) => {
        const exists = prev.some((s) => normaliserTexte(s) === normaliserTexte(nom));
        if (exists) return prev;
        const updated = [...prev, nom].sort((a, b) => a.localeCompare(b, 'fr'));
        return updated;
      });
    } catch {
      // silent fail, at least add to local list
      setSpecialites((prev) => {
        const exists = prev.some((s) => normaliserTexte(s) === normaliserTexte(nom));
        if (exists) return prev;
        return [...prev, nom].sort((a, b) => a.localeCompare(b, 'fr'));
      });
    }
  };

  const query = normaliserTexte(inputValue.trim());

  const filtered = specialites
    .filter((s) => normaliserTexte(s).includes(query))
    .sort((a, b) => {
      const aStarts = normaliserTexte(a).startsWith(query) ? 0 : 1;
      const bStarts = normaliserTexte(b).startsWith(query) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return a.localeCompare(b, 'fr');
    });

  const isNewSpecialite = inputValue.trim() !== '' &&
    !specialites.some((s) => normaliserTexte(s) === query);

  const selectSpecialite = (spec: string) => {
    setInputValue(spec);
    onChange(spec);
    setIsOpen(false);
    setHighlightIndex(-1);
    inputRef.current?.blur();

    // Si c'est une spécialité absente de la liste, on l'ajoute automatiquement en base
    if (!specialites.some((s) => normaliserTexte(s) === normaliserTexte(spec))) {
      saveNewSpecialite(spec);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const max = filtered.length + (isNewSpecialite ? 1 : 0) - 1;
      setHighlightIndex((prev) => Math.min(prev + 1, max));
      if (!isOpen) setIsOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightIndex >= 0) {
        if (isNewSpecialite && highlightIndex === 0) {
          selectSpecialite(inputValue.trim());
        } else {
          const idx = isNewSpecialite ? highlightIndex - 1 : highlightIndex;
          if (filtered[idx]) selectSpecialite(filtered[idx]);
        }
      } else if (inputValue.trim()) {
        selectSpecialite(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  const handleBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      if (isSelectingRef.current) {
        isSelectingRef.current = false;
        return;
      }
      const trimmed = inputValue.trim();
      if (trimmed) {
        // Ajout automatique en base si la spécialité n'existe pas encore
        if (!specialites.some((s) => normaliserTexte(s) === normaliserTexte(trimmed))) {
          saveNewSpecialite(trimmed);
        }
        if (trimmed !== value) {
          onChange(trimmed);
        }
      }
      setIsOpen(false);
    }, 150);
  };

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          required={required}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setHighlightIndex(-1);
          }}
          onFocus={() => {
            if (suppressNextFocusRef.current) {
              suppressNextFocusRef.current = false;
              return;
            }
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder || 'Tapez ou sélectionnez une spécialité...'}
          className="w-full px-4 py-3 pr-10 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
        />
        <button
          type="button"
          onMouseDown={(e) => {
            // Empêche le blur de l'input avant le toggle
            e.preventDefault();
            if (isOpen) {
              // On va fermer : supprimer le prochain onFocus pour éviter la réouverture
              suppressNextFocusRef.current = true;
            }
            setIsOpen((prev) => !prev);
            // Garder le focus sur l'input sans le refocaliser si déjà focus
            if (document.activeElement !== inputRef.current) {
              inputRef.current?.focus();
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-clinic-blue transition-colors cursor-pointer"
        >
          {isOpen ? (
            <i className="ri-arrow-up-s-line text-lg"></i>
          ) : (
            <i className="ri-arrow-down-s-line text-lg"></i>
          )}
        </button>
      </div>

      {isOpen && (filtered.length > 0 || isNewSpecialite) && (
        <ul className="absolute z-50 left-0 right-0 mt-2 max-h-64 overflow-y-auto bg-white border-2 border-clinic-blue/20 rounded-2xl shadow-xl">
          {isNewSpecialite && (
            <li
              className={`px-4 py-3 text-sm text-clinic-blue font-medium border-b border-gray-100 cursor-pointer hover:bg-clinic-blue/5 flex items-center ${
                highlightIndex === 0 ? 'bg-clinic-blue/10 font-semibold' : ''
              }`}
              onPointerDown={() => { isSelectingRef.current = true; }}
              onClick={() => {
                selectSpecialite(inputValue.trim());
              }}
              onMouseEnter={() => setHighlightIndex(0)}
            >
              <i className="ri-add-circle-line mr-2 text-lg flex-shrink-0"></i>
              <span className="truncate">Ajouter "{inputValue.trim()}"</span>
            </li>
          )}
          {filtered.map((spec, idx) => {
            const actualIdx = isNewSpecialite ? idx + 1 : idx;
            return (
              <li
                key={spec}
                className={`px-4 py-3 text-sm cursor-pointer flex items-center ${
                  actualIdx === highlightIndex ? 'bg-clinic-blue/10 text-clinic-blue-dark font-medium' : 'text-gray-700 hover:bg-clinic-blue/5'
                }`}
                onPointerDown={() => { isSelectingRef.current = true; }}
                onClick={() => {
                  selectSpecialite(spec);
                }}
                onMouseEnter={() => setHighlightIndex(actualIdx)}
              >
                <i className="ri-stethoscope-line mr-2 text-base flex-shrink-0 text-clinic-blue/60"></i>
                <span className="truncate">{spec}</span>
                {value === spec && (
                  <i className="ri-check-line ml-auto text-clinic-blue flex-shrink-0"></i>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}